import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { Check, ArrowRight, Calendar, Users, MapPin, Bell, Share2, BarChart3 } from 'lucide-react'
import { APP_URL } from '@/lib/public-trpc'
import { capability, DFY_BUNDLES } from '@gjl/voice/pricing'

const ticketing = capability('ticketing')!
const concierge = DFY_BUNDLES.find((b) => b.id === 'concierge')!

const features = [
  {
    icon: Calendar,
    title: 'Event Pages',
    description:
      'Create beautiful, shareable event pages with all the details—program, artists, venue, tickets. One link to share everywhere.',
  },
  {
    icon: Users,
    title: 'RSVP Management',
    description:
      'Track who is attending your events. See RSVPs in real-time and know exactly how many seats to prepare.',
  },
  {
    icon: MapPin,
    title: 'Venue Management',
    description:
      'Save your regular venues with address, capacity, and notes. Reuse them across events without retyping.',
  },
  {
    icon: Bell,
    title: 'Automatic Reminders',
    description: 'Send email reminders before events. Customize timing and message. Reduce no-shows automatically.',
  },
  {
    icon: Share2,
    title: 'Easy Sharing',
    description:
      'Share event pages on social media with rich previews. Add to calendar buttons for Google, Apple, and Outlook.',
  },
  {
    icon: BarChart3,
    title: 'Event Analytics',
    description:
      'Track page views, RSVPs, and attendance. See which events perform best and learn what your audience wants.',
  },
]

const useCases = [
  {
    title: 'Concert Series',
    description:
      'Create a page for each concert in your season. Link them together, share the calendar, and track attendance across your entire series.',
  },
  {
    title: 'Rehearsals & Meetings',
    description:
      'Not just for public events. Track internal rehearsals, board meetings, and planning sessions. Keep your team organized.',
  },
  {
    title: 'Recurring Events',
    description:
      'Weekly rehearsals? Monthly concerts? Create recurring events and manage the whole series from one place.',
  },
]

const otherFeatures = [
  { title: 'Website', href: '/platform/features/website', description: 'Build your site' },
  { title: 'Email', href: '/platform/features/email', description: 'Send newsletters' },
  { title: 'Donations', href: '/platform/features/donations', description: 'Accept support' },
  { title: 'Analytics', href: '/platform/features/analytics', description: 'Track growth' },
]

export default function EventsFeaturePage() {
  useEffect(() => {
    document.title = 'Event Management for Music Organizations | Fugue Lab App'
  }, [])

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const featuresRef = useStaggeredGrid<HTMLDivElement>('.feature-card', { stagger: 0.1 })
  const useCasesRef = useStaggeredGrid<HTMLDivElement>('.use-case-card', { stagger: 0.1 })
  const pricingRef = useStaggeredGrid<HTMLDivElement>('.pricing-card', { stagger: 0.15 })
  const otherFeaturesRef = useStaggeredGrid<HTMLDivElement>('.other-feature-card', { stagger: 0.1 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="px-4 py-20">
        <div ref={heroRef} className="container mx-auto max-w-4xl text-center">
          <div className="bg-primary/10 text-primary mb-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
            <Calendar className="h-4 w-4" />
            Event Management
          </div>
          <h1 className="mb-6 text-4xl font-bold md:text-5xl">
            Beautiful event pages
            <br />
            that fill seats
          </h1>
          <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-xl">
            Create stunning event pages for your concerts, rehearsals, and performances. Manage RSVPs, send reminders,
            and track attendance—all in one place.
          </p>
          <div className="flex flex-col justify-center gap-4 sm:flex-row">
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Create Your First Event <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
            <Link to="/platform">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 text-lg">
                  See All Features
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground mt-4 text-sm">
            Ticketing is ${ticketing.priceMonthly}/month plus {ticketing.perUnitFee}.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Everything for Event Success</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              From planning to post-event follow-up, manage your entire event lifecycle.
            </p>
          </div>
          <div ref={featuresRef} className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="feature-card border-border bg-card rounded-lg border p-6"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <feature.icon className="text-primary mb-4 h-10 w-10" />
                <h3 className="mb-2 text-lg font-semibold">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Event Page Preview */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Share One Link, Get Full Details</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl">
              Every event gets its own beautiful page with all the information your audience needs.
            </p>
          </div>
          <motion.div
            className="bg-card border-border space-y-6 rounded-2xl border p-8"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="space-y-4">
              <div className="text-muted-foreground flex items-center gap-3 text-sm">
                <Calendar className="h-4 w-4" />
                <span>Saturday, March 15, 2025 at 7:30 PM</span>
              </div>
              <h3 className="text-2xl font-bold">Spring Concert: Mozart & Beethoven</h3>
              <div className="text-muted-foreground flex items-center gap-3 text-sm">
                <MapPin className="h-4 w-4" />
                <span>Symphony Hall, 123 Main St</span>
              </div>
            </div>
            <div className="prose prose-sm text-muted-foreground">
              <p>
                Join us for an evening of classical masterworks featuring Mozart Symphony No. 40 and Beethoven Piano
                Concerto No. 5, performed by our award-winning orchestra.
              </p>
            </div>
            <div className="flex gap-4">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button className="cursor-pointer">RSVP Free</Button>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" className="cursor-pointer">
                  Add to Calendar
                </Button>
              </motion.div>
            </div>
            <div className="border-border border-t pt-4">
              <p className="text-muted-foreground text-sm">
                <strong>24 people</strong> are attending
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-muted/30 px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Built for Music Organizations</h2>
            <p className="text-muted-foreground">
              Whether you are running concerts, rehearsals, or meetings—we have got you covered.
            </p>
          </div>
          <div ref={useCasesRef} className="grid gap-8 md:grid-cols-3">
            {useCases.map((useCase) => (
              <motion.div
                key={useCase.title}
                className="use-case-card text-center"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <h3 className="mb-2 text-lg font-semibold">{useCase.title}</h3>
                <p className="text-muted-foreground text-sm">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 py-20">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold">Simple Pricing</h2>
          </div>
          <div ref={pricingRef} className="mx-auto grid max-w-3xl gap-8 md:grid-cols-2">
            <motion.div
              className="pricing-card border-primary/50 bg-primary/5 rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">Set up and run by our team</div>
              <h3 className="mb-2 text-2xl font-bold">{ticketing.name}</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${ticketing.priceMonthly}</span>
                <span className="text-muted-foreground">/month + {ticketing.perUnitFee}</span>
              </div>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Selling a ticket is free apart from the 1.5%</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Ticket tiers and capacity limits</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">QR check-in and reporting</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Our team sets it up and runs it for you today</span>
                </li>
              </ul>
              <Link to="/get-started" className="block">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button className="w-full cursor-pointer">Get Started</Button>
                </motion.div>
              </Link>
            </motion.div>
            <motion.div
              className="pricing-card border-border bg-card rounded-2xl border p-8"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="text-primary mb-2 text-sm font-medium">Done for you</div>
              <h3 className="mb-2 text-2xl font-bold">Box office, run for you</h3>
              <div className="mb-4 flex items-baseline gap-1">
                <span className="text-2xl font-bold">{concierge.name}</span>
                <span className="text-muted-foreground">{concierge.price}</span>
              </div>
              <ul className="mb-8 space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Event setup handled for you</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">On-sale management</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Reporting after every event</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />
                  <span className="text-muted-foreground">Check-in support</span>
                </li>
              </ul>
              <Link to="/services/done-for-you#concierge" className="block">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <Button variant="outline" className="w-full cursor-pointer">
                    See Concierge
                  </Button>
                </motion.div>
              </Link>
            </motion.div>
          </div>
          <div className="mt-8 text-center">
            <Link to="/packages" className="text-primary font-medium hover:underline">
              See All Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Other Features */}
      <section className="bg-muted/30 px-4 py-16">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-8 text-center">
            <h2 className="text-2xl font-bold">More Features</h2>
            <p className="text-muted-foreground">Events are just the beginning.</p>
          </div>
          <div ref={otherFeaturesRef} className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {otherFeatures.map((feature) => (
              <motion.div
                key={feature.href}
                className="other-feature-card"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <Link
                  to={feature.href}
                  className="border-border bg-card hover:border-primary block h-full rounded-lg border p-4 text-center transition-colors"
                >
                  <h3 className="font-semibold">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary/5 px-4 py-16">
        <div ref={ctaRef} className="container mx-auto max-w-3xl text-center">
          <div className="cta-content">
            <h2 className="mb-4 text-3xl font-bold">Ready to create your first event?</h2>
            <p className="text-muted-foreground mb-8 text-xl">
              Join music organizations using Fugue Lab to fill seats at every performance.
            </p>
            <a href={`${APP_URL}/auth`}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                className="inline-block"
              >
                <Button size="lg" className="cursor-pointer px-8 text-lg">
                  Create Your First Event <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </motion.div>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
